import { getTileId, scoreTile } from '~/algorithms/letters';
import type { GameMode } from '~/store/game.svelte';
import type { Board, Coord, Match, Tile, Word } from '~/types';

export const stringToWord = (word: string): Word => ({
  word: word.split('').map((letter) => letterToTile(letter)),
  score: 1,
});

export const wordToString = (word: Pick<Match, 'word'>) =>
  word.word.map((t) => t.letter).join('');

export const reverseString = (s: string) => s.split('').reverse().join('');

export const coordToStr = (c: Coord) => c.join(',');

export const coordStr2Int = (c: string): Coord =>
  c.split(',').map((i) => parseInt(i)) as unknown as Coord;

export const letterToTile = (letter: string) => ({
  letter: letter.toUpperCase(),
  id: getTileId(),
  points: scoreTile(letter, 'daily'),
});

export const sortByLength = (a: Match, b: Match) =>
  b.word.length - a.word.length;

export const getTranslate = (x: number, y: number) => `
  transform: translate3d(${x}px, ${y}px, 0);
`;

export const getWordCenter = (coords: Coord[]): Coord => {
  const bbox = [
    [coords[0][0], coords[0][1]],
    [coords[coords.length - 1][0], coords[coords.length - 1][1]],
  ];
  const center = [
    (1 + bbox[1][0] + bbox[0][0]) / 2,
    (1 + bbox[1][1] + bbox[0][1]) / 2,
  ] as Coord;
  return center;
};

export const getFlick = ([i1, j1]: Coord, [i2, j2]: Coord) => {
  const dx = i1 - i2;
  const dy = j1 - j2;
  const distance = Math.sqrt(dx * dx + dy * dy);
  // const directions = ['right', 'down-right', 'down', 'down-left', 'left', 'up-left', 'up', 'up-right'];
  const directions = [
    [1, 0],
    [1, 1],
    [0, 1],
    [-1, 1],
    [-1, 0],
    [-1, -1],
    [0, -1],
    [1, -1],
  ];
  const angle = (1 + Math.atan2(dy, dx) / Math.PI) * 4;
  const direction = directions[Math.round(angle) % 8];
  return { distance, direction };
};

export type TileSpec = string | { letter: string; multiplier?: number };

export const lettersToBoard = (
  letters: TileSpec[][],
  mode: GameMode = 'classic',
): Board<Tile> =>
  letters.map((col) =>
    col.map((spec) => {
      const letter = typeof spec === 'string' ? spec : spec.letter;
      const multiplier = typeof spec === 'string' ? 1 : (spec.multiplier ?? 1);
      return {
        letter: letter.toUpperCase(),
        points: scoreTile(letter, mode),
        id: getTileId(),
        multiplier,
      };
    }),
  );

// we only need to check the coords along the same axes
export const getCoordsToCheck = (board: Board, [[i, j], [i2, j2]]: [Coord, Coord]) => (
  [
    ...new Set(
      [
        ...board.map((v, i) => [
          [i, j],
          [i, j2],
        ]),
        ...board[0].map((v, j) => [
          [i, j],
          [i2, j],
        ]),
      ]
        .flat()
        .filter((c) => c)
        .map((c) => c.join()),
    ),
  ].map(coordStr2Int)
)

export const isIOS = () =>
  (/iPad|iPhone|iPod/.test(navigator.platform) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
  // @ts-ignore
  !window.MSStream;

export const range = (start: number, end: number) => {
  const r = end - start;
  return [...Array(r).keys()].map((i) => i + start);
};
